// LapseBanner (Story 10.5, UX-DR16 — AC 1, 2, 4, 6).
//
// Two modes on the `Banner` primitive:
//   - countdown (14/7/1 window): tier-colored, dismissible for the session PER
//     TIER, so an escalation (info→warn→danger) re-shows the notice. No client
//     mockup exists — copy follows the story's field contract (validate at UAT).
//   - lapsed (post day-zero acknowledgment): permanent `warn` banner with the
//     Screen-02-validated copy verbatim; no dismiss affordance.
//
// "Renew now" stays a `secondary` button here — the banner must not steal the
// view's single primary CTA (design system); the modal owns the primary variant.

import { useState } from "react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";

import { useRenewNow } from "@/hooks/queries/useRenewNow";

import { type LapseTier } from "./useLapseStatus";

const DISMISS_KEY_PREFIX = "franklin:lapse-banner-dismissed:";

const RENEW_ERROR_COPY = "Couldn't open the renewal page. Please try again.";

/**
 * User-scoped per-tier dismissal key. Scoping by user id keeps one clinician's
 * dismissal from pre-dismissing the next sign-in on a shared clinical
 * workstation (same tab-session, different user).
 */
function dismissKey(userId: string, tier: LapseTier): string {
  return `${DISMISS_KEY_PREFIX}${userId}:${tier}`;
}

function readDismissed(userId: string, tier: LapseTier): boolean {
  try {
    return sessionStorage.getItem(dismissKey(userId, tier)) === "true";
  } catch {
    return false; // private mode / storage disabled — never crash the shell.
  }
}

function writeDismissed(userId: string, tier: LapseTier): void {
  try {
    sessionStorage.setItem(dismissKey(userId, tier), "true");
  } catch {
    /* private mode — dismissal is best-effort */
  }
}

/**
 * Dismiss unmounts this banner (the component returns null), which would drop
 * keyboard focus to `<body>` — silently resetting a keyboard/AT user's position
 * (ValidationSummaryBanner precedent: a control that removes itself must move
 * focus to a stable target). Both host shells expose a focusable `<main>`.
 */
function focusMainContent(): void {
  document.querySelector<HTMLElement>("main")?.focus();
}

/** `{date}` formatted like the Profile renewal date (Story 10.8 contract). */
function formatLapseDate(validUntil: Date): string {
  return validUntil.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export type LapseBannerProps = { className?: string } & (
  | {
      mode: "countdown";
      /** Scopes the session dismissal flags (shared-workstation safety). */
      userId: string;
      tier: LapseTier;
      daysLeft: number;
      validUntil: Date;
    }
  | { mode: "lapsed" }
);

export function LapseBanner(props: LapseBannerProps) {
  const { renewNow, isPending, isError } = useRenewNow();
  // Per-tier session dismissal (AC6): storage covers remounts, local state covers
  // the current render pass; a tier change reads the new key → escalation re-shows.
  const [dismissedTier, setDismissedTier] = useState<LapseTier | null>(null);

  const renewButton = (
    <Button variant="secondary" size="sm" onClick={renewNow} disabled={isPending}>
      Renew now
    </Button>
  );
  const renewError = isError ? (
    <p className="text-sm text-[color:var(--text-error)]" role="alert">
      {RENEW_ERROR_COPY}
    </p>
  ) : null;

  if (props.mode === "lapsed") {
    return (
      <Banner
        variant="warn"
        title="Your subscription has lapsed."
        data-testid="lapse-banner-permanent"
        className={props.className}
      >
        <p>
          Existing records remain accessible, but new clients and evaluations are
          disabled until billing is renewed.
        </p>
        <div className="mt-2 flex items-center gap-3">{renewButton}</div>
        {renewError}
      </Banner>
    );
  }

  const { userId, tier, daysLeft, validUntil } = props;
  if (dismissedTier === tier || readDismissed(userId, tier)) return null;

  const headline =
    tier === "danger"
      ? "Subscription ends tomorrow."
      : `Subscription renewal in ${daysLeft} days.`;

  return (
    <Banner variant={tier} title={headline} data-testid="lapse-banner" className={props.className}>
      <p>
        Your access ends on {formatLapseDate(validUntil)}. Renew now to avoid
        interruption.
      </p>
      <div className="mt-2 flex items-center gap-3">
        {renewButton}
        <Button
          variant="ghost"
          size="sm"
          aria-label="Dismiss subscription renewal notice"
          onClick={() => {
            writeDismissed(userId, tier);
            setDismissedTier(tier);
            focusMainContent();
          }}
        >
          Dismiss
        </Button>
      </div>
      {renewError}
    </Banner>
  );
}
